import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MagnifyingGlass, Check, HardHat, Plus, MapPin } from "@phosphor-icons/react";
import PlacePicker from "@/components/PlacePicker";
import { CHANTIER_TYPE_META } from "@/pages/chantier/constants";

/**
 * Chantier picker with live autocomplete + inline "create chantier" mini dialog.
 * When no match is found for the typed query, offering a form to enter address
 * + multi-domain types before creating — so techs never have to open another
 * page just to attach an address or classify the trades involved.
 */
export default function ChantierSearchWithCreate({ value, onPick, placeholder, testidPrefix = "chantier-search" }) {
  const [chantiers, setChantiers] = useState([]);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try { const r = await api.get("/chantiers"); setChantiers(r.data || []); }
      catch (err) { console.debug("[ch-search] fetch failed:", err?.message || err); }
    })();
  }, []);

  useEffect(() => {
    if (value && !query) {
      const c = chantiers.find((x) => x.id === value);
      if (c) setQuery(c.name || "");
    }
  }, [value, chantiers, query]);

  const q = query.trim().toLowerCase();
  const matches = q
    ? chantiers.filter((c) => (c.name || "").toLowerCase().includes(q) || (c.address || "").toLowerCase().includes(q))
    : chantiers.slice(0, 12);
  const exactMatch = q && chantiers.some((c) => (c.name || "").toLowerCase() === q);
  const showCreate = q.length >= 2 && !exactMatch;

  const pick = (c) => {
    setQuery(c.name || "");
    setOpen(false);
    onPick?.(c);
  };

  const onCreated = (created) => {
    setChantiers((s) => [created, ...s]);
    pick(created);
  };

  return (
    <div className="relative">
      <MagnifyingGlass size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
      <Input
        value={query}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); if (value) onPick?.(null); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder={placeholder || "Nom du chantier — ou tape pour en créer un"}
        className="bg-white/5 border-white/10 pl-9"
        data-testid={`${testidPrefix}-input`}
      />
      {value && (
        <Check size={13} className="absolute right-3 top-1/2 -translate-y-1/2 text-emerald-400" weight="bold" />
      )}
      {open && (matches.length > 0 || showCreate) && (
        <div
          className="absolute z-40 left-0 right-0 mt-1 bg-neutral-950/95 backdrop-blur-md border border-white/15 rounded-xl shadow-2xl overflow-hidden max-h-64 overflow-y-auto"
          data-testid={`${testidPrefix}-list`}
        >
          {matches.slice(0, 8).map((c) => (
            <button
              key={c.id}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(c)}
              className="w-full text-left px-3 py-2 flex items-start gap-2 border-b border-white/5 last:border-0 hover:bg-white/[0.04]"
              data-testid={`${testidPrefix}-item-${c.id}`}
            >
              <div className="w-6 h-6 rounded-lg bg-[#005CFF]/20 flex items-center justify-center shrink-0 mt-0.5">
                <HardHat size={11} weight="duotone" className="text-[#7CB2FF]" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{c.name}</div>
                {c.address && <div className="text-[10px] text-white/50 truncate">{c.address}</div>}
              </div>
            </button>
          ))}
          {showCreate && (
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => { setCreateOpen(true); setOpen(false); }}
              className="w-full text-left px-3 py-2.5 flex items-center gap-2 bg-emerald-500/10 hover:bg-emerald-500/20 border-t border-emerald-500/30"
              data-testid={`${testidPrefix}-create`}
            >
              <div className="w-6 h-6 rounded-lg bg-emerald-500/25 flex items-center justify-center shrink-0">
                <Plus size={11} weight="bold" className="text-emerald-300" />
              </div>
              <div className="text-sm text-emerald-200">
                Créer le chantier <b>« {query.trim()} »</b>
              </div>
            </button>
          )}
        </div>
      )}
      <CreateChantierMiniDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        initialName={query.trim()}
        onCreated={onCreated}
        testidPrefix={testidPrefix}
      />
    </div>
  );
}

/**
 * Mini creation dialog — name + address (PlacePicker) + multi-domain types.
 * A single chantier can now span several trades: électricité + Loxone,
 * climatisation + vidéosurveillance, etc.
 */
function CreateChantierMiniDialog({ open, onOpenChange, initialName, onCreated, testidPrefix }) {
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [addressMeta, setAddressMeta] = useState({}); // {postal_code, city, latitude, longitude}
  const [types, setTypes] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setName(initialName || "");
      setAddress("");
      setAddressMeta({});
      setTypes([]);
    }
  }, [open, initialName]);

  const toggleType = (t) => setTypes((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]));

  const onPickAddress = (p) => {
    setAddress(p.address || p.label || "");
    setAddressMeta({
      postal_code: p.postal_code || null,
      city: p.city || null,
      latitude: p.latitude ?? null,
      longitude: p.longitude ?? null,
    });
  };

  const submit = async () => {
    if (!name.trim()) { toast.error("Nom du chantier requis"); return; }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        status: "waiting",
        progress: 0,
        types,
        ...(address ? { address, ...addressMeta } : {}),
      };
      const r = await api.post("/chantiers", payload);
      toast.success(`Chantier « ${r.data.name} » créé`);
      onCreated?.(r.data);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-neutral-950 border-white/10 max-w-md" data-testid={`${testidPrefix}-create-dialog`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <HardHat size={18} weight="duotone" className="text-[#7CB2FF]" /> Nouveau chantier
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-white/50">Nom *</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="bg-white/5 border-white/10 h-10 mt-1"
              data-testid={`${testidPrefix}-create-name`}
              autoFocus
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-white/50 flex items-center gap-1.5">
              <MapPin size={11} weight="duotone" /> Adresse
            </Label>
            <div className="mt-1">
              <PlacePicker
                value={address}
                onChange={setAddress}
                onPick={onPickAddress}
                placeholder="Rechercher une adresse…"
                testid={`${testidPrefix}-create-address`}
              />
            </div>
            {addressMeta.postal_code && addressMeta.city && (
              <div className="text-[9px] text-emerald-400 mt-1">
                📍 {addressMeta.postal_code} {addressMeta.city}
              </div>
            )}
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-white/50">
              Domaines <span className="text-white/40 lowercase tracking-normal ml-1">(un ou plusieurs)</span>
            </Label>
            <div className="grid grid-cols-2 gap-2 mt-2" data-testid={`${testidPrefix}-create-types`}>
              {Object.entries(CHANTIER_TYPE_META).map(([v, m]) => {
                const on = types.includes(v);
                return (
                  <button
                    key={v}
                    type="button"
                    onClick={() => toggleType(v)}
                    className="h-10 rounded-xl border text-xs font-semibold transition-all flex items-center justify-center gap-1"
                    style={on
                      ? { background: m.color + "22", borderColor: m.color, color: "#fff" }
                      : { background: "rgba(255,255,255,0.03)", borderColor: "rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.6)" }}
                    data-testid={`${testidPrefix}-create-type-${v}`}
                  >
                    <span>{m.icon}</span>
                    {m.label}
                  </button>
                );
              })}
            </div>
          </div>
          <Button
            onClick={submit}
            disabled={saving || !name.trim()}
            className="w-full h-11 bg-[#005CFF] hover:bg-[#0047CC] gap-2"
            data-testid={`${testidPrefix}-create-submit`}
          >
            <Plus size={14} weight="bold" />
            {saving ? "Création…" : "Créer le chantier"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
